import React, { useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
  ComposedChart,
  ScatterChart,
  Scatter
} from 'recharts';
import { Box, Typography, CircularProgress, Skeleton, Alert, GlobalStyles, FormControl, InputLabel, Select, MenuItem, Chip, SelectChangeEvent, ToggleButton, ToggleButtonGroup, IconButton, Tooltip as MuiTooltip, Switch, FormControlLabel } from '@mui/material';
import { Visibility, VisibilityOff, TrendingUp, TrendingDown, ShowChart, StackedLineChart, InfoOutlined } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { AnalyticsChartProps } from '../types/analytics.types';

// Global styles to override Recharts hover backgrounds
const chartHoverStyles = (
  <GlobalStyles
    styles={{
      // Override Recharts default hover background
      '.recharts-active-shape': {
        opacity: '0.95 !important',
      },
      '.recharts-bar:hover': {
        opacity: '0.9 !important',
      },
      '.recharts-cartesian-grid-bg': {
        '&:hover': {
          fill: 'transparent !important',
        },
      },
      // More subtle background for bar hover
      '.recharts-bar-rectangle:hover': {
        opacity: '0.9 !important',
      },
      // Override the default light gray hover background
      '.recharts-active-bar': {
        opacity: '0.9 !important',
      },
      '.recharts-tooltip-cursor': {
        fill: 'rgba(0, 0, 0, 0.02) !important', // Very subtle gray
      },
      // For area and line charts
      '.recharts-active-area': {
        opacity: '0.9 !important',
      },
      '.recharts-active-line': {
        opacity: '0.9 !important',
      },
    }}
  />
);

// Styled components
const ChartContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 400,
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(4), // Add space between charts to prevent legend from being cut off by tables below
}));

const ChartHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
  minHeight: 40,
}));

const ChartTitle = styled(Box)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: '#18181b',
  letterSpacing: '-0.01em',
}));

const ChartSubtitle = styled(Box)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 400,
  color: '#52525b',
  marginTop: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 400,
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 500, // Increased height to accommodate chart content
  backgroundColor: 'transparent',
  paddingBottom: theme.spacing(3), // Increased padding to ensure legend doesn't get cut off by tables below
  overflow: 'visible', // Ensure legend is not clipped
}));

// Default colors for charts - orange and red color scheme with distinct shades
const DEFAULT_COLORS = [
  '#f97316', // orange-500 - bright orange
  '#dc2626', // red-600 - vibrant red
  '#ea580c', // orange-600 - deeper orange
  '#b91c1c', // red-700 - deep red
  '#fb923c', // orange-400 - lighter orange
  '#991b1b', // red-800 - dark red
  '#fd7e14', // orange-600 variant - medium orange
  '#ef4444', // red-500 - bright red
];

// Calculate smart axis interval based on data density
const calculateAxisInterval = (dataLength: number): number | 'preserveStartEnd' => {
  if (dataLength <= 7) return 0;           // Show all labels for 7 or fewer points
  if (dataLength <= 14) return 1;          // Show every 2nd label for 8-14 points  
  if (dataLength <= 30) return 2;          // Show every 3rd label for 15-30 points
  if (dataLength <= 60) return 4;          // Show every 5th label for 31-60 points
  return 'preserveStartEnd';                // Show start/end only for 60+ points
};

// Custom tooltip component - shadcn style
const CustomTooltip = ({ active, payload, label, config, useDualYAxis }: any) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  // Get the raw data point to access both date formats
  const dataPoint = payload[0]?.payload;

  // Check if this is a percentage stacked chart
  const isPercentageStack = config.stackType === 'percentage' || config.showAsPercentage;

  // Determine the best date display
  let dateDisplay = label; // Default to label (dateFormatted)

  if (dataPoint) {
    // If we have both dateFormatted and date, show both
    // Use timeZone: 'UTC' to match global date range filter - avoids off-by-one for users west of UTC
    const utcOptions = { timeZone: 'UTC' as const };
    if (dataPoint.dateFormatted && dataPoint.date) {
      const originalDate = new Date(dataPoint.date);
      if (!isNaN(originalDate.getTime())) {
        // Show formatted date with full date in parentheses
        dateDisplay = `${dataPoint.dateFormatted} (${originalDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          ...utcOptions
        })})`;
      }
    } else if (dataPoint.dateFormatted) {
      dateDisplay = dataPoint.dateFormatted;
    } else if (dataPoint.date) {
      const date = new Date(dataPoint.date);
      if (!isNaN(date.getTime())) {
        dateDisplay = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          ...utcOptions
        });
      }
    }
  }

  // Separate enterprise from location values when dual axis is active
  const locationEntries = useDualYAxis ? payload.filter((e: any) => !e.name.includes('Enterprise')) : payload;
  const enterpriseEntry = useDualYAxis ? payload.find((e: any) => e.name.includes('Enterprise')) : null;

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #e4e4e7',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
        maxWidth: 280,
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          mb: 1,
          fontSize: '14px',
          color: '#18181b',
          fontFamily: "'Inter', sans-serif",
        }}
      >
        {dateDisplay}
      </Typography>

      {/* Location values section */}
      {useDualYAxis && locationEntries.length > 0 && (
        <>
          <Typography
            variant="caption"
            sx={{
              fontSize: '10px',
              color: '#71717a',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.5,
              display: 'block'
            }}
          >
            Locations (Left Axis)
          </Typography>
          {locationEntries.map((entry: any, index: number) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: index < locationEntries.length - 1 ? 0.5 : 0,
                pl: 1,
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: entry.color || '#f97316',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: '#71717a',
                  fontSize: '12px',
                  fontFamily: "'Inter', sans-serif",
                  '& span': {
                    color: '#18181b',
                    fontWeight: 600,
                    ml: 0.5,
                  }
                }}
              >
                {formatOrderTypeValue(entry.name)}: <span>{formatTooltipValue(entry.value, config, dataPoint, entry.dataKey)}</span>
              </Typography>
            </Box>
          ))}
        </>
      )}

      {/* Enterprise value section */}
      {useDualYAxis && enterpriseEntry && (
        <>
          <Typography
            variant="caption"
            sx={{
              fontSize: '10px',
              color: '#71717a',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mt: 1,
              mb: 0.5,
              display: 'block'
            }}
          >
            Enterprise Total (Right Axis)
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pl: 1,
            }}
          >
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: enterpriseEntry.color || '#1f2937',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: '#71717a',
                fontSize: '12px',
                fontFamily: "'Inter', sans-serif",
                '& span': {
                  color: '#18181b',
                  fontWeight: 600,
                  ml: 0.5,
                }
              }}
            >
              {formatOrderTypeValue(enterpriseEntry.name)}: <span>{formatTooltipValue(enterpriseEntry.value, config, dataPoint, enterpriseEntry.dataKey)}</span>
            </Typography>
          </Box>
        </>
      )}

      {/* Original layout when no dual axis */}
      {!useDualYAxis && payload.map((entry: any, index: number) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: index < payload.length - 1 ? 0.5 : 0,
          }}
        >
          <Box
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: entry.color || '#f97316',
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: '#71717a',
              fontSize: '12px',
              fontFamily: "'Inter', sans-serif",
              '& span': {
                color: '#18181b',
                fontWeight: 600,
                ml: 0.5,
              }
            }}
          >
            {formatOrderTypeValue(entry.name)}: <span>{formatTooltipValue(entry.value, config, dataPoint, entry.dataKey)}</span>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// Format rounded values (e.g., 5.3k instead of $5,309)
const formatRoundedValue = (value: number, isCurrency: boolean = false): string => {
  if (value >= 1000000) {
    const rounded = (value / 1000000).toFixed(1);
    return isCurrency ? `$${rounded}M` : `${rounded}M`;
  } else if (value >= 1000) {
    const rounded = (value / 1000).toFixed(1);
    return isCurrency ? `$${rounded}k` : `${rounded}k`;
  } else {
    return isCurrency ? `$${Math.round(value)}` : `${Math.round(value)}`;
  }
};

// Format tooltip values (show full precision in tooltips)
const formatTooltipValue = (value: any, config: any, dataPoint?: any, fieldName?: string) => {
  if (typeof value !== 'number') return value;

  // Check if this is a percentage stacked chart
  const isPercentageStack = config.stackType === 'percentage' || config.showAsPercentage;

  // Check if it's a revenue/currency field
  const yAxisConfig = Array.isArray(config?.yAxis) ? config.yAxis[0] : config?.yAxis;
  const isCurrency =
    yAxisConfig?.label?.toLowerCase().includes('revenue') ||
    yAxisConfig?.label?.toLowerCase().includes('sales') ||
    yAxisConfig?.label?.includes('$') ||
    yAxisConfig?.format === 'currency';

  // For ranked-band-line charts with percentage mode, the value IS already the percentage
  if (config.chartType === 'ranked-band-line' && isPercentageStack) {
    return `${value.toFixed(2)}%`;
  }

  if (isPercentageStack && dataPoint && fieldName) {
    // Calculate percentage from raw values (for stacked-bar charts)
    const metrics = config.metrics || [];
    const totalValue = metrics.reduce((sum: number, metric: string) => {
      const val = dataPoint[metric];
      return sum + (typeof val === 'number' ? val : 0);
    }, 0);

    const rawValue = dataPoint[fieldName];
    if (totalValue > 0 && typeof rawValue === 'number') {
      const percentage = (rawValue / totalValue) * 100;

      // Format the actual amount
      let actualAmount = '';
      if (isCurrency) {
        actualAmount = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(rawValue);
      } else {
        actualAmount = rawValue.toLocaleString();
      }

      return `${percentage.toFixed(1)}% (${actualAmount})`;
    }
  }

  if (isCurrency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  return value.toLocaleString();
};

// Format axis values with rounding
const formatAxisValue = (value: any, config?: any) => {
  if (typeof value !== 'number') return value;

  const isCurrency = config?.format === 'currency' ||
    config?.label?.toLowerCase().includes('revenue') ||
    config?.label?.toLowerCase().includes('sales') ||
    config?.label?.includes('$');

  if (config?.format === 'percentage') {
    return `${value}%`;
  }

  // Use rounded values for axis labels
  return formatRoundedValue(value, isCurrency);
};

// Format order type values for display (e.g., DINEIN -> DINE-IN)
const formatOrderTypeValue = (value: string): string => {
  if (typeof value === 'string' && value === 'DINEIN') {
    return 'DINE-IN';
  }
  return value;
};

// Helper to get Y-axis config (handles both single axis and array)
const getYAxisConfig = (yAxisConfig: any, index: number = 0) => {
  if (Array.isArray(yAxisConfig)) {
    return yAxisConfig[index] || yAxisConfig[0];
  }
  return yAxisConfig;
};

// Pre-calculated label visibility map (field+dataIndex -> should show label)
let shouldShowLabelMap: Map<string, 'max' | 'min' | null> = new Map();
let isAreaChartMode: boolean = false; // Flag to determine chart type for label formatting
const DISTANCE_FROM_POINT = 12; // Exact distance from data point in pixels

// Custom label component to display dollar amounts at data points
const createCustomLabel = (config: any, seriesIndex: number = 0, seriesColor?: string, fieldName?: string, highlightedSeries?: string | null, filteredData?: any[]) => {
  return (props: any) => {
    const { x, y, value, payload, index } = props;

    // For stacked bars, Recharts may not pass payload, so use filteredData[index] instead
    const dataPoint = payload || (filteredData && index !== undefined ? filteredData[index] : null);

    if (value === null || value === undefined) return null;

    // Check if this series should be dimmed (not highlighted)
    const isDimmed = highlightedSeries && fieldName && highlightedSeries !== fieldName;

    // Hide labels for dimmed series to reduce clutter
    if (isDimmed) return null;

    // Check if this is a percentage stacked area chart
    const isPercentageStack = config.stackType === 'percentage' || config.showAsPercentage;

    let formattedValue: string;
    let individualPercentageValue: number | null = null; // Track percentage for positioning

    if (isPercentageStack) {
      // For percentage stacked charts, always calculate from raw payload values
      // Recharts passes cumulative normalized values (0-1) which represent stack position,
      // not the individual segment's percentage of the total. We need to use payload[fieldName]
      // to get the true individual segment value.

      if (dataPoint) {
        // Always use cumulative subtraction when value is normalized (0-1) from Recharts
        // Recharts passes cumulative normalized values for percentage stacked charts
        const metrics = config.metrics || [];

        // Get all numeric fields if metrics not defined
        let metricsToUse = metrics;
        if (metricsToUse.length === 0 && dataPoint) {
          metricsToUse = Object.keys(dataPoint).filter(key => typeof dataPoint[key] === 'number' && !['date', 'dateFormatted', 'id', 'timeKey', 'level', 'parentId'].includes(key));
        }

        // Calculate total from all segments
        const totalValue = dataPoint ? metricsToUse.reduce((sum: number, metric: string) => {
          const val = dataPoint[metric];
          return sum + (typeof val === 'number' ? val : 0);
        }, 0) : 0;

        if (totalValue > 0 && typeof value === 'number' && value > 0 && value <= 1) {
          // Find the index of the current field in the metrics array
          let fieldIndex = -1;
          if (fieldName) {
            fieldIndex = metricsToUse.indexOf(fieldName);
          }

          // If fieldName not found or not provided, use seriesIndex
          if (fieldIndex < 0 && seriesIndex !== undefined && seriesIndex >= 0) {
            fieldIndex = seriesIndex;
          }

          // If still not found, try to match by cumulative value
          if (fieldIndex < 0 || fieldIndex >= metricsToUse.length) {
            let cumulative = 0;
            for (let i = 0; i < metricsToUse.length; i++) {
              const field = metricsToUse[i];
              if (dataPoint && typeof dataPoint[field] === 'number') {
                cumulative += dataPoint[field] / totalValue;
                if (Math.abs(cumulative - value) < 0.01) {
                  fieldIndex = i;
                  break;
                }
              }
            }
          }

          if (fieldIndex >= 0 && fieldIndex < metricsToUse.length) {
            // Calculate cumulative normalized value of all previous segments
            let previousCumulative = 0;
            for (let i = 0; i < fieldIndex; i++) {
              const prevField = metricsToUse[i];
              if (prevField && dataPoint && typeof dataPoint[prevField] === 'number') {
                previousCumulative += dataPoint[prevField] / totalValue;
              }
            }

            // Current value is cumulative up to this segment, subtract previous to get individual
            const individualPercentage = (value - previousCumulative) * 100;

            if (individualPercentage >= 3) {
              individualPercentageValue = individualPercentage;
              formattedValue = `${Math.round(individualPercentage)}%`;
            } else {
              return null;
            }
          } else {
            // Fallback: use value directly (first segment)
            const percentage = value * 100;
            if (percentage < 3) return null;
            individualPercentageValue = percentage;
            formattedValue = `${Math.round(percentage)}%`;
          }
        } else {
          // Value is not normalized or payload doesn't have valid data
          return null;
        }
      } else if (dataPoint && typeof value === 'number' && value > 0 && value <= 1) {
        // We have dataPoint but not fieldName - use cumulative subtraction with seriesIndex or find fieldName from value
        const metrics = config.metrics || [];

        // Get all numeric fields if metrics not defined
        let metricsToUse = metrics;
        if (metricsToUse.length === 0 && dataPoint) {
          metricsToUse = Object.keys(dataPoint).filter(key => typeof dataPoint[key] === 'number' && !['date', 'dateFormatted', 'id', 'timeKey', 'level', 'parentId'].includes(key));
        }

        // Calculate total from all segments
        const totalValue = dataPoint ? metricsToUse.reduce((sum: number, metric: string) => {
          const val = dataPoint[metric];
          return sum + (typeof val === 'number' ? val : 0);
        }, 0) : 0;

        if (totalValue > 0) {
          // Find the field index - use seriesIndex if available, otherwise try to match by value
          let fieldIndex = seriesIndex !== undefined ? seriesIndex : -1;

          // If seriesIndex not reliable, try to find which field this value corresponds to
          if (fieldIndex < 0 || fieldIndex >= metricsToUse.length) {
            // Calculate cumulative values to find matching index
            let cumulative = 0;
            for (let i = 0; i < metricsToUse.length; i++) {
              const field = metricsToUse[i];
              if (dataPoint && typeof dataPoint[field] === 'number') {
                cumulative += dataPoint[field] / totalValue;
                // Check if this cumulative value matches (with small tolerance)
                if (Math.abs(cumulative - value) < 0.01) {
                  fieldIndex = i;
                  break;
                }
              }
            }
          }

          if (fieldIndex >= 0 && fieldIndex < metricsToUse.length) {
            // Calculate cumulative normalized value of all previous segments
            let previousCumulative = 0;
            for (let i = 0; i < fieldIndex; i++) {
              const prevField = metricsToUse[i];
              if (prevField && dataPoint && typeof dataPoint[prevField] === 'number') {
                previousCumulative += dataPoint[prevField] / totalValue;
              }
            }

            // Current value is cumulative up to this segment, so subtract previous cumulative
            // to get individual segment percentage
            const individualPercentage = (value - previousCumulative) * 100;

            if (individualPercentage >= 3) {
              individualPercentageValue = individualPercentage;
              formattedValue = `${Math.round(individualPercentage)}%`;
            } else {
              return null;
            }
          } else {
            // Can't determine field, use value directly
            const percentage = value * 100;
            if (percentage < 3) return null;
            individualPercentageValue = percentage;
            formattedValue = `${Math.round(percentage)}%`;
          }
        } else {
          return null;
        }
      } else {
        // Fallback: when dataPoint/fieldName not fully available, calculate individual segment by cumulative subtraction
        // Recharts passes cumulative normalized values (0-1) representing stack position up to this segment
        // We need to subtract all previous segments' cumulative values to get the individual segment percentage

        if (typeof value === 'number' && value > 0 && value <= 1) {
          let individualPercentage: number;

          // Try to use dataPoint if available to calculate cumulative subtraction
          if (dataPoint && seriesIndex !== undefined && seriesIndex >= 0) {
            const metrics = config.metrics || [];

            // Get all numeric fields if metrics not defined
            let metricsToUse = metrics;
            if (metricsToUse.length === 0 && dataPoint) {
              metricsToUse = Object.keys(dataPoint).filter(key => typeof dataPoint[key] === 'number' && !['date', 'dateFormatted', 'id', 'timeKey', 'level', 'parentId'].includes(key));
            }

            // Calculate total from all segments
            const totalValue = dataPoint ? metricsToUse.reduce((sum: number, metric: string) => {
              const val = dataPoint[metric];
              return sum + (typeof val === 'number' ? val : 0);
            }, 0) : 0;

            if (totalValue > 0 && seriesIndex > 0) {
              // Calculate cumulative normalized value of all previous segments
              let previousCumulative = 0;
              for (let i = 0; i < seriesIndex; i++) {
                const prevField = metricsToUse[i];
                if (prevField && dataPoint && typeof dataPoint[prevField] === 'number') {
                  previousCumulative += dataPoint[prevField] / totalValue;
                }
              }

              // Current value is cumulative up to this segment, so subtract previous cumulative
              // to get individual segment percentage
              individualPercentage = (value - previousCumulative) * 100;
              individualPercentageValue = individualPercentage;
            } else if (seriesIndex === 0) {
              // First segment: value is already the individual segment percentage
              individualPercentage = value * 100;
              individualPercentageValue = individualPercentage;
            } else {
              // Fallback to direct conversion if we can't calculate properly
              individualPercentage = value * 100;
              individualPercentageValue = individualPercentage;
            }
          } else {
            // No payload available: use value directly (first segment) or can't calculate accurately
            // For first segment (seriesIndex === 0), value represents individual percentage
            // For later segments without payload, we can't accurately calculate, so use value as-is
            individualPercentage = value * 100;
            individualPercentageValue = individualPercentage;
          }

          // Only show labels for segments >= 3%
          if (individualPercentage < 3) return null;
          formattedValue = `${Math.round(individualPercentage)}%`;
        } else if (typeof value === 'number' && value > 1) {
          // If value is > 1, it might be a raw value that needs to be calculated differently
          // This case is less common but handle it gracefully
          return null;
        } else {
          return null;
        }
      }
    } else {
      // Original formatting logic for non-percentage charts
      const yAxisConfig = Array.isArray(config?.yAxis) ? config.yAxis[0] : config?.yAxis;
      const dataFieldName = payload?.dataKey || yAxisConfig?.field || '';
      const isCurrency =
        yAxisConfig?.label?.toLowerCase().includes('revenue') ||
        yAxisConfig?.label?.toLowerCase().includes('sales') ||
        yAxisConfig?.label?.includes('$') ||
        yAxisConfig?.format === 'currency' ||
        dataFieldName?.toLowerCase().includes('sales') ||
        dataFieldName?.toLowerCase().includes('revenue') ||
        dataFieldName?.toLowerCase().includes('total_sales') ||
        dataFieldName?.toLowerCase().includes('net_sales');

      if (isAreaChartMode) {
        // Area chart: use exact formatted value (no rounding)
        if (typeof value === 'number' && isCurrency) {
          formattedValue = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(value);
        } else {
          formattedValue = typeof value === 'number' ? value.toLocaleString() : value;
        }
      } else {
        // Line chart: use rounded value (e.g., 5.3k)
        formattedValue = typeof value === 'number' && isCurrency
          ? formatRoundedValue(value, true)
          : typeof value === 'number'
            ? formatRoundedValue(value, false)
            : value;
      }
    }

    // Show labels for all numeric fields (currency and count/metric values)
    if (typeof value !== 'number') return null;

    const dataPointY = y; // The actual Y position of the data point

    // Position labels
    let finalY: number;

    if (isPercentageStack) {
      // For percentage stacks: center label in the segment area
      // Estimate chart height and calculate segment height based on percentage
      const estimatedChartHeight = 350; // Approximate pixel height of chart drawing area
      const segmentHeightPx = individualPercentageValue ? (individualPercentageValue / 100) * estimatedChartHeight : 0;

      // Position label slightly above center
      finalY = dataPointY + (segmentHeightPx / 2) - 30;
    } else {
      // For regular charts: check pre-calculated map to see if this label should be shown
      const labelKey = `${fieldName}-${index}`;
      const labelType = shouldShowLabelMap.get(labelKey);

      if (!labelType) {
        return null; // Don't show this label
      }

      // Position: max above, min below
      finalY = labelType === 'max'
        ? dataPointY - DISTANCE_FROM_POINT
        : dataPointY + DISTANCE_FROM_POINT;
    }

    return (
      <g>
        <text
          x={x}
          y={finalY}
          fill={seriesColor || "#18181b"}
          fontSize="12"
          fontFamily="'Inter', sans-serif"
          fontWeight={600}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ pointerEvents: 'none' }}
        >
          {formattedValue}
        </text>
      </g>
    );
  };
};

export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({
  data,
  config,
  loading = false,
  error,
  className,
  visualization,
  filterValues,
  onFilterChange,
  showCreditCardDetail: controlledShowCreditCardDetail,
  showThirdPartyDetail: controlledShowThirdPartyDetail,
  onCreditCardDetailChange,
  onThirdPartyDetailChange
}) => {
  // State for top/bottom toggle when splitLocations is enabled
  const [topBottomView, setTopBottomView] = useState<'top' | 'bottom'>('top');
  // State for enterprise line visibility toggle (default OFF)
  const [showEnterpriseToggle, setShowEnterpriseToggle] = useState<boolean>(false);
  // State for highlighted series (null = none highlighted, string = field name of highlighted series)
  const [highlightedSeries, setHighlightedSeries] = useState<string | null>(null);
  // State for chart type toggle (line vs area)
  const [activeChartType, setActiveChartType] = useState<string>(config.chartType || 'line');
  // State for metric toggle (Net Sales vs Percentage)
  const [metricMode, setMetricMode] = useState<string>(config.metricToggle?.defaultMode || 'net_sales');

  // Use controlled props if provided, otherwise fall back to internal state
  const [internalShowCreditCardDetail, setInternalShowCreditCardDetail] = useState<boolean>(false);
  const [internalShowThirdPartyDetail, setInternalShowThirdPartyDetail] = useState<boolean>(false);

  const showCreditCardDetail = controlledShowCreditCardDetail !== undefined
    ? controlledShowCreditCardDetail
    : internalShowCreditCardDetail;
  const showThirdPartyDetail = controlledShowThirdPartyDetail !== undefined
    ? controlledShowThirdPartyDetail
    : internalShowThirdPartyDetail;

  const handleCreditCardDetailChange = (value: boolean) => {
    if (onCreditCardDetailChange) {
      onCreditCardDetailChange(value);
    } else {
      setInternalShowCreditCardDetail(value);
    }
  };

  const handleThirdPartyDetailChange = (value: boolean) => {
    if (onThirdPartyDetailChange) {
      onThirdPartyDetailChange(value);
    } else {
      setInternalShowThirdPartyDetail(value);
    }
  };

  // Debug: log first few data points and config axes
  try {
    const sample = Array.isArray(data) ? data.slice(0, 3) : [];
    if (sample.length) {
      const parsed = sample.map(d => {
        const raw = d.date;
        const base = typeof raw === 'string' && raw.includes('T') ? raw.split('T')[0] : raw;
        const parts = typeof base === 'string' ? base.split('-').map(Number) : [];
        const local = parts.length === 3 ? new Date(parts[0], parts[1] - 1, parts[2]) : (raw ? new Date(raw as any) : null);
        return { raw, local: local ? local.toISOString() : null };
      });
    }
  } catch { }


  if (loading) {
    return (
      <LoadingContainer className={className}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: '8px' }} />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className={className}>
        {error}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert severity="warning" className={className}>
        No data available for chart
      </Alert>
    );
  }



  const colors = config.colors || DEFAULT_COLORS;

  // Clear label visibility map at the start of each render
  shouldShowLabelMap.clear();
  isAreaChartMode = false;

  let activeConfig: any = { ...config };

  // 🚀 NEW: Dynamic series generation for calculated mode charts
  const getDynamicSeries = () => {
    // If we have configured series, always return them (even if data is empty)
    if (activeConfig.series && activeConfig.series.length > 0) {
      const maxSeries = activeConfig.maxSeries || Infinity;
      return maxSeries === Infinity ? activeConfig.series : activeConfig.series.slice(0, maxSeries);
    }

    // For calculated mode without configured series, need data to generate dynamic series
    if (activeConfig.mode !== 'calculated' || !data.length) {
      return [];
    }

    // Union keys from all rows (pivoted chart data is sparse; row[0] omits locations)
    const standardFields = ['date', 'dateFormatted', 'id', 'timeKey', 'level', 'parentId', 'hasChildren', 'expanded', 'recordCount'];
    const keySet = new Set<string>();
    for (const row of data) {
      for (const key of Object.keys(row)) {
        if (!standardFields.includes(key)) keySet.add(key);
      }
    }
    const dynamicFields = Array.from(keySet).sort((a, b) => a.localeCompare(b));

    // Get maxSeries limit from config, default to allowing all series
    const maxSeries = activeConfig.maxSeries || Infinity;

    // Check if groupBy includes dimensions that would create pivoted fields (like order_type, location)
    // When data is pivoted, we should use dynamic fields instead of activeConfig.metrics
    const hasGroupByDimensions = activeConfig.groupBy && activeConfig.groupBy.length > 0 &&
      activeConfig.groupBy.some(dim => dim !== 'time' && dim !== 'date');

    // If activeConfig.metrics exists AND metrics match actual data fields, use them
    // Otherwise, fall through to dynamic fields (especially for pivoted data)
    if (activeConfig.metrics && activeConfig.metrics.length > 0 && !hasGroupByDimensions) {
      // Check if metrics actually exist in the data
      const metricsExistInData = activeConfig.metrics.some(metric => dynamicFields.includes(metric));

      if (metricsExistInData || chartFilters.length > 0) {
        // Use activeConfig.metrics when they match data fields or when filters are present (payment channels)
        const metricsSeries = activeConfig.metrics.map((field, index) => {
          const baseName = field.includes(' ') ? field : field.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          return {
            name: formatOrderTypeValue(baseName),
            field: field,
            type: activeConfig.chartType === 'line' ? 'line' : activeConfig.chartType === 'area' ? 'area' : 'bar',
            color: colors[index % colors.length],
            strokeWidth: 2,
            yAxisIndex: undefined,
            lineWidth: undefined
          };
        });
        // Apply maxSeries limit if specified
        return maxSeries === Infinity ? metricsSeries : metricsSeries.slice(0, maxSeries);
      }
    }

    // Generate dynamic series for each field (for charts using groupBy dimensions like order_type)
    // This handles pivoted data where fields like DINE_IN, TAKE_OUT become separate series
    // ENTERPRISE is handled separately and not counted against maxSeries limit

    // Separate ENTERPRISE from regular fields
    const enterpriseField = dynamicFields.find(f => f === 'ENTERPRISE');
    const regularFields = dynamicFields.filter(f => f !== 'ENTERPRISE');

    // When splitLocations is enabled, don't pre-slice - keep all fields for proper top/bottom calculation
    const fieldsToShow = (maxSeries === Infinity || activeConfig.splitLocations) ? regularFields : regularFields.slice(0, maxSeries);

    const dynamicSeries = fieldsToShow.map((field, index) => {
      const baseName = field.includes(' ') ? field : field.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

      return {
        name: formatOrderTypeValue(baseName),
        field: field,
        type: activeConfig.chartType === 'line' ? 'line' : activeConfig.chartType === 'area' ? 'area' : 'bar',
        color: colors[index % colors.length],
        strokeWidth: 2,
        yAxisIndex: undefined,
        lineWidth: undefined
      };
    });

    // Add ENTERPRISE as an extra line (not counted in maxSeries) if it exists and toggle is ON
    // Only include if showEnterpriseToggle is true (user has enabled it via toggle)
    if (enterpriseField && showEnterpriseToggle) {
      dynamicSeries.push({
        name: 'Enterprise',
        field: enterpriseField,
        type: config.chartType === 'line' ? 'line' : config.chartType === 'area' ? 'area' : 'bar',
        color: '#1f2937',
        strokeWidth: 3,
        strokeDasharray: '8 4' as any,
        yAxisIndex: undefined,
        lineWidth: undefined
      } as any);
    }

    return dynamicSeries;
  };

  // Apply top/bottom filtering when splitLocations is enabled
  const getFilteredDataByToggle = (seriesList: any[]) => {
    // If showTopAndBottom is enabled, data is already filtered on backend - no client-side filtering needed
    if (config.showTopAndBottom) {
      return seriesList;
    }

    const splitLocations = config.splitLocations;
    if (!splitLocations || !data.length) {
      return seriesList;
    }

    // Filter out ENTERPRISE from the location series for top/bottom calculation
    const locationSeries = seriesList.filter(s => s.field !== 'ENTERPRISE');
    const enterpriseSeries = seriesList.find(s => s.field === 'ENTERPRISE');

    if (locationSeries.length === 0) {
      return seriesList;
    }

    // Calculate totals for each location to determine top/bottom
    const seriesTotals: Record<string, number> = {};
    locationSeries.forEach(series => {
      seriesTotals[series.field] = data.reduce((sum, row) => {
        return sum + (Number(row[series.field]) || 0);
      }, 0);
    });

    const sortedAll = Object.entries(seriesTotals).sort(([, a], [, b]) => b - a);
    const positiveDesc = sortedAll.filter(([, v]) => v > 0);
    const positiveAsc = [...positiveDesc].sort(([, a], [, b]) => a - b);

    const maxSeries = config.maxSeries || 5;
    const totalLocations = sortedAll.length;

    // Smart splitting logic
    let topCount: number, bottomCount: number;

    if (totalLocations <= maxSeries) {
      topCount = totalLocations;
      bottomCount = totalLocations;
    } else if (totalLocations <= maxSeries * 2) {
      topCount = Math.ceil(totalLocations / 2);
      bottomCount = totalLocations - topCount;
    } else {
      topCount = maxSeries;
      bottomCount = maxSeries;
    }

    // When showing a subset, use totals > 0 only (matches calculated KPI ranking)
    let selectedFields: string[];
    if (totalLocations <= maxSeries) {
      selectedFields = sortedAll.map(([field]) => field);
    } else if (topBottomView === 'top') {
      selectedFields = positiveDesc.slice(0, Math.min(topCount, positiveDesc.length)).map(([field]) => field);
    } else {
      selectedFields = positiveAsc.slice(0, Math.min(bottomCount, positiveAsc.length)).map(([field]) => field);
    }

    // Filter series to only include selected locations + enterprise if toggle is ON
    const filteredSeries = locationSeries.filter(s => selectedFields.includes(s.field));

    // Only include enterprise if toggle is enabled
    return (enterpriseSeries && showEnterpriseToggle) ? [...filteredSeries, enterpriseSeries] : filteredSeries;
  };

  // Calculate dynamic counts for toggle button labels
  const getTopBottomCounts = () => {
    if (!config.splitLocations || !data.length) {
      return { topCount: config.maxSeries || 5, bottomCount: config.maxSeries || 5 };
    }

    // Get all location series (excluding ENTERPRISE)
    const allSeries = getDynamicSeries();
    const locationCount = allSeries.filter(s => s.field !== 'ENTERPRISE').length;
    const maxSeries = config.maxSeries || 5;

    if (locationCount <= maxSeries) {
      return { topCount: locationCount, bottomCount: locationCount };
    } else if (locationCount <= maxSeries * 2) {
      const topCount = Math.ceil(locationCount / 2);
      const bottomCount = locationCount - topCount;
      return { topCount, bottomCount };
    } else {
      return { topCount: maxSeries, bottomCount: maxSeries };
    }
  };

  const effectiveSeries = getFilteredDataByToggle(getDynamicSeries());

  // Handle chart-level filters
  const chartFilters = config.filters || [];

  // Process filtered data based on chart filters and splitLocations toggle
  const getFilteredData = () => {
    let filteredData = data;

    // Handle chart filters (like payment methods)
    if (chartFilters.length && filterValues) {
      const paymentFilter = chartFilters.find(f => f.id === 'payment-mix-toggle');
      if (paymentFilter && filterValues[paymentFilter.id]) {
        // Return original data - filtering will be done at series level
        return filteredData;
      }
    }

    // Filter data columns to only include selected series when splitLocations is enabled
    if (config.splitLocations && filteredData.length > 0) {
      const selectedFields = new Set(effectiveSeries.map(s => s.field));
      const standardFields = ['date', 'dateFormatted', 'timeKey', 'recordCount'];

      filteredData = filteredData.map(row => {
        const filteredRow: any = {};
        // Keep standard fields
        standardFields.forEach(field => {
          if (row.hasOwnProperty(field)) {
            filteredRow[field] = row[field];
          }
        });
        // Keep only selected series fields
        selectedFields.forEach(field => {
          if (row.hasOwnProperty(field)) {
            filteredRow[field] = row[field];
          }
        });
        // Preserve ENTERPRISE field only if toggle is ON
        if (showEnterpriseToggle && row.hasOwnProperty('ENTERPRISE') && !filteredRow.hasOwnProperty('ENTERPRISE')) {
          filteredRow['ENTERPRISE'] = row['ENTERPRISE'];
        }
        return filteredRow;
      });
    }

    return filteredData;
  };

  // Get filtered series based on selected metrics
  const getFilteredSeries = () => {
    // For toggle filters, don't filter series - toggle only affects data aggregation
    const toggleFilter = chartFilters.find(f => f.type === 'toggle');
    if (toggleFilter) {
      // Toggle filters keep all series, they just change how data is aggregated
      return effectiveSeries;
    }

    // For multi-select filters, filter series by selected metrics
    const multiSelectFilter = chartFilters.find(f => f.type === 'multi-select');
    if (multiSelectFilter && filterValues?.[multiSelectFilter.id]) {
      const selectedMetrics = filterValues[multiSelectFilter.id] as string[];
      return effectiveSeries.filter(series => selectedMetrics.includes(series.field));
    }

    return effectiveSeries;
  };

  let filteredData = getFilteredData();
  const filteredSeries = getFilteredSeries();


  // Determine effective chart type
  const effectiveChartType = config.chartTypeToggle ? activeChartType : config.chartType;
  const isAreaChart = effectiveChartType === 'area';

  // Set the global flag for label formatting
  isAreaChartMode = isAreaChart;

  // Pre-calculate which labels should be shown
  // Area charts: show ALL labels, Line charts: show only highest and lowest per time point
  filteredData.forEach((dataPoint, dataIndex) => {
    // Get all numeric values at this time point across all series
    const valuesAtThisTime: Array<{ field: string; value: number }> = [];

    filteredSeries.forEach(series => {
      const value = dataPoint[series.field];
      if (typeof value === 'number' && !isNaN(value)) {
        valuesAtThisTime.push({ field: series.field, value });
      }
    });

    if (valuesAtThisTime.length === 0) return;

    if (isAreaChart) {
      // Area chart: show ALL labels
      valuesAtThisTime.forEach(({ field }) => {
        const labelKey = `${field}-${dataIndex}`;
        shouldShowLabelMap.set(labelKey, 'max'); // Use 'max' positioning for all area chart labels
      });
    } else {
      // Line chart: show only highest and lowest
      const maxValue = Math.max(...valuesAtThisTime.map(v => v.value));
      const minValue = Math.min(...valuesAtThisTime.map(v => v.value));

      valuesAtThisTime.forEach(({ field, value }) => {
        const labelKey = `${field}-${dataIndex}`;
        if (value === maxValue) {
          shouldShowLabelMap.set(labelKey, 'max');
        } else if (value === minValue && minValue !== maxValue) {
          shouldShowLabelMap.set(labelKey, 'min');
        }
      });
    }
  });

  // Handle legend click for highlighting series
  const handleLegendClick = (data: any) => {
    const clickedField = data.dataKey || data.value;
    // Toggle: if already highlighted, unhighlight; otherwise highlight the clicked one
    setHighlightedSeries(prev => prev === clickedField ? null : clickedField);
  };

  // Get opacity for a series based on highlight state
  const getSeriesOpacity = (fieldName: string): number => {
    if (!highlightedSeries) return 1; // No highlighting active
    return highlightedSeries === fieldName ? 1 : 0.15; // Highlighted = full, others = more dimmed
  };

  // Get stroke width for a series based on highlight state
  const getSeriesStrokeWidth = (fieldName: string, baseWidth: number): number => {
    if (!highlightedSeries) return baseWidth;
    return highlightedSeries === fieldName ? baseWidth + 2 : baseWidth; // More prominent stroke increase
  };

  // Custom legend renderer with highlighting
  const renderCustomLegend = (props: any) => {
    const { payload } = props;
    if (!payload || !payload.length) return null;

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        paddingTop: '8px', // Reduced from 20px
        paddingBottom: '24px', // Increased bottom padding to prevent legend from being cut off
        marginBottom: '12px', // Additional margin for spacing
        fontSize: '14px'
      }}>
        {payload.map((entry: any, index: number) => {
          const isHighlighted = highlightedSeries === entry.dataKey;
          const isActive = !highlightedSeries || isHighlighted;

          return (
            <div
              key={`legend-${index}`}
              onClick={() => handleLegendClick(entry)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                opacity: isActive ? 1 : 0.4,
                transition: 'all 0.2s ease',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: isHighlighted ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                border: isHighlighted ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid transparent',
              }}
            >
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: entry.color,
                borderRadius: '50%',
                border: isHighlighted ? '2px solid white' : 'none',
                boxShadow: isHighlighted ? '0 0 8px rgba(255, 255, 255, 0.6)' : 'none',
              }} />
              <span style={{
                color: isActive ? '#18181b' : '#9ca3af',
                fontWeight: isHighlighted ? 600 : 400,
              }}>
                {formatOrderTypeValue(entry.value)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Render chart filters
  const renderChartFilters = () => {
    if (!chartFilters.length) return null;

    return (
      <FilterContainer>
        {chartFilters.map(filter => {
          // Debug: Log filter info
          if (filter.type === 'toggle') {
            console.log('Toggle filter found:', filter.id, filter.label, filter.options);
          }
          if (filter.type === 'multi-select') {
            const currentValue = filterValues?.[filter.id] || filter.defaultValue || [];

            return (
              <FormControl key={filter.id} size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{filter.label}</InputLabel>
                <Select
                  multiple
                  value={currentValue}
                  label={filter.label}
                  onChange={(event: SelectChangeEvent<string[]>) => {
                    onFilterChange?.(filter.id, event.target.value as string[]);
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => {
                        const option = filter.options?.find(opt => opt.value === value);
                        return (
                          <Chip
                            key={value}
                            label={option?.label || value}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {filter.options?.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          }

          if (filter.type === 'toggle') {
            const currentValue = filterValues?.[filter.id] || filter.defaultValue || filter.options?.[0]?.value;

            return (
              <Box key={filter.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {filter.label && (
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#52525b', fontWeight: 600 }}>
                    {filter.label}:
                  </Typography>
                )}
                <ToggleButtonGroup
                  value={currentValue}
                  exclusive
                  onChange={(event, newValue) => {
                    if (newValue !== null) {
                      onFilterChange?.(filter.id, newValue);
                    }
                  }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      fontSize: '0.8125rem',
                      padding: '6px 14px',
                      textTransform: 'none',
                      border: '1px solid #e4e4e7',
                      color: '#52525b',
                      fontWeight: 500,
                      backgroundColor: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#fef3f2',
                        borderColor: '#fb923c'
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#f97316',
                        color: 'white',
                        borderColor: '#f97316',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: '#ea580c',
                          borderColor: '#ea580c'
                        }
                      }
                    }
                  }}
                >
                  {filter.options?.map(option => (
                    <ToggleButton key={option.value} value={option.value}>
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            );
          }

          return null;
        })}
      </FilterContainer>
    );
  };

  // Render chart based on type
  const renderChart = () => {
    // Use activeChartType if toggle is enabled, otherwise use config.chartType
    const effectiveChartType = config.chartTypeToggle ? activeChartType : config.chartType;

    switch (effectiveChartType) {
      case 'line':
        const lineYAxisConfig = Array.isArray(config.yAxis) ? config.yAxis[0] : config.yAxis;
        const linePrimaryColor = colors[0] || '#f97316';

        // Determine if we need dual Y-axes (when enterprise is shown)
        const lineHasEnterprise = effectiveSeries?.some((s: any) => s.field === 'ENTERPRISE');
        const lineUseDualYAxis = lineHasEnterprise && showEnterpriseToggle;

        return (
          <AreaChart data={filteredData} margin={{ top: 60, right: 40, bottom: 60, left: 40 }}>
            <defs>
              {filteredSeries?.length ? (
                // Multi-series gradients
                filteredSeries.map((series, index) => {
                  const seriesColor = series.color || colors[index % colors.length];
                  const gradientId = `line-gradient-${seriesColor.replace('#', '')}-${index}`;
                  return (
                    <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={seriesColor} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={seriesColor} stopOpacity={0} />
                    </linearGradient>
                  );
                })
              ) : (
                // Single-series gradient
                <linearGradient id={`line-gradient-${linePrimaryColor.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={linePrimaryColor} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={linePrimaryColor} stopOpacity={0} />
                </linearGradient>
              )}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" strokeOpacity={0.5} />
            <XAxis
              dataKey={config.xAxis.field}
              tick={{ fontSize: 12, fill: '#71717a' }}
              tickFormatter={(value) => formatAxisValue(value, config.xAxis)}
              height={60}
              interval={0}
              angle={-45}
              textAnchor="end"
              axisLine={{ stroke: '#e4e4e7' }}
            />

            {/* Left Y-Axis - for location lines */}
            <YAxis
              yAxisId="left"
              tick={lineUseDualYAxis ? { fontSize: 12, fill: '#71717a' } : false}
              width={lineUseDualYAxis ? 60 : 0}
              tickFormatter={lineUseDualYAxis ? (value) => formatAxisValue(value, lineYAxisConfig) : undefined}
              axisLine={lineUseDualYAxis ? { stroke: '#e4e4e7' } : false}
              label={lineUseDualYAxis ? {
                value: 'Locations',
                angle: -90,
                position: 'insideLeft',
                offset: 10,
                style: { fontSize: 11, fill: '#71717a', fontWeight: 600, textAnchor: 'middle' }
              } : undefined}
            />

            {/* Right Y-Axis - for enterprise line */}
            {lineUseDualYAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12, fill: '#71717a' }}
                width={60}
                tickFormatter={(value) => formatAxisValue(value, lineYAxisConfig)}
                axisLine={{ stroke: '#e4e4e7' }}
                label={{
                  value: 'Enterprise',
                  angle: 90,
                  position: 'insideRight',
                  offset: 10,
                  style: { fontSize: 11, fill: '#71717a', fontWeight: 600, textAnchor: 'middle' }
                }}
              />
            )}

            <Tooltip
              content={<CustomTooltip config={config} useDualYAxis={lineUseDualYAxis} />}
              cursor={{ stroke: linePrimaryColor, strokeWidth: 1, strokeDasharray: '3 3' }}
            />
            {config.legend?.show && (
              <Legend
                content={renderCustomLegend}
                align={config.legend.align === 'start' ? 'left' : config.legend.align === 'end' ? 'right' : 'center'}
                verticalAlign={config.legend.position === 'top' ? 'top' : 'bottom'}
              />
            )}
            {effectiveSeries?.length ? (
              // Multi-series line chart with gradient fills
              effectiveSeries.map((series, index) => {
                const seriesColor = series.color || colors[index % colors.length];
                const gradientId = `line-gradient-${seriesColor.replace('#', '')}-${index}`;
                const isEnterprise = series.field === 'ENTERPRISE';
                const opacity = getSeriesOpacity(series.field);
                const strokeWidth = getSeriesStrokeWidth(series.field, series.strokeWidth || 2);

                // Assign yAxisId based on whether it's enterprise
                const yAxisId = (lineUseDualYAxis && isEnterprise) ? 'right' : 'left';

                return (
                  <Area
                    key={series.name}
                    type="monotone"
                    dataKey={series.field}
                    yAxisId={yAxisId}
                    stroke={seriesColor}
                    fill={`url(#${gradientId})`}
                    strokeWidth={strokeWidth}
                    strokeDasharray={(series as any).strokeDasharray}
                    fillOpacity={opacity}
                    strokeOpacity={opacity}
                    name={formatOrderTypeValue(series.name)}
                    dot={{ r: 3, opacity }}
                    activeDot={{ r: 5 }}
                    label={createCustomLabel(config, index, seriesColor, series.field, highlightedSeries, filteredData)}
                    isAnimationActive={false}
                    animationDuration={0}
                  />
                );
              })
            ) : (
              // Simple single-series line chart with gradient fill
              <Area
                type="monotone"
                dataKey={lineYAxisConfig.field}
                yAxisId="left"
                stroke={linePrimaryColor}
                fill={`url(#line-gradient-${linePrimaryColor.replace('#', '')})`}
                strokeWidth={2}
                fillOpacity={1}
                name={formatOrderTypeValue(lineYAxisConfig.label || 'Value')}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                label={createCustomLabel(config, 0, linePrimaryColor, lineYAxisConfig.field, highlightedSeries, filteredData)}
                isAnimationActive={false}
                animationDuration={0}
              />
            )}
          </AreaChart>
        );

      case 'bar':
        const barYAxisConfig = Array.isArray(config.yAxis) ? config.yAxis[0] : config.yAxis;
        const isPercentageStackBar = config.stackType === 'percentage' || config.showAsPercentage;
        const isTimeBasedBar = config.xAxis?.type === 'time' || config.xAxis?.field === 'date' || config.xAxis?.field === 'dateFormatted';

        return (
          <BarChart
            data={filteredData}
            margin={{ top: 60, right: 40, bottom: 60, left: 40 }}
            stackOffset={isPercentageStackBar ? "expand" : "none"}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" strokeOpacity={0.5} />
            <XAxis
              dataKey={config.xAxis.field}
              tick={{ fontSize: 12, fill: '#71717a' }}
              tickFormatter={(value) => formatAxisValue(value, config.xAxis)}
              height={60}
              interval={isTimeBasedBar ? 0 : calculateAxisInterval(filteredData.length)}
              angle={-45}
              textAnchor="end"
              axisLine={{ stroke: '#e4e4e7' }}
            />
            <YAxis
              tick={isPercentageStackBar ? { fontSize: 12, fill: '#71717a' } : false}
              width={isPercentageStackBar ? 60 : 0}
              tickFormatter={isPercentageStackBar ? (value) => `${(value * 100).toFixed(0)}%` : undefined}
              domain={isPercentageStackBar ? [0, 1] : undefined}
              axisLine={isPercentageStackBar ? { stroke: '#e4e4e7' } : false}
            />
            <Tooltip
              content={<CustomTooltip config={config} />}
              cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
            />
            {config.legend?.show && (
              <Legend
                content={renderCustomLegend}
                align={config.legend.align === 'start' ? 'left' : config.legend.align === 'end' ? 'right' : 'center'}
                verticalAlign={config.legend.position === 'top' ? 'top' : 'bottom'}
              />
            )}
            {filteredSeries?.length ? (
              // Multi-series bar chart with stacking support
              filteredSeries.map((series, index) => {
                const seriesColor = series.color || colors[index % colors.length];
                const opacity = getSeriesOpacity(series.field);
                return (
                  <Bar
                    key={series.name}
                    dataKey={series.field}
                    fill={seriesColor}
                    fillOpacity={opacity}
                    name={formatOrderTypeValue(series.name)}
                    stackId={config.stacked ? 'stack' : undefined}
                    label={(config as any).showLabels ? createCustomLabel(config, index, seriesColor, series.field, highlightedSeries, filteredData) : undefined}
                    isAnimationActive={false}
                    animationDuration={0}
                  />
                );
              })
            ) : (
              // Simple single-series bar chart
              <Bar
                dataKey={barYAxisConfig.field}
                fill={colors[0]}
                name={formatOrderTypeValue(barYAxisConfig.label || 'Value')}
                label={(config as any).showLabels ? createCustomLabel(config, 0, colors[0], barYAxisConfig.field, highlightedSeries, filteredData) : undefined}
                isAnimationActive={false}
                animationDuration={0}
              />
            )}
          </BarChart>
        );

      case 'stacked-bar':
        // Dedicated stacked bar chart optimized for time-series data
        const stackedBarYAxisConfig = Array.isArray(config.yAxis) ? config.yAxis[0] : config.yAxis;
        const isPercentageStacked = config.stackType === 'percentage' || config.showAsPercentage;

        return (
          <BarChart
            data={filteredData}
            margin={{ top: 60, right: 40, bottom: 60, left: 40 }}
            stackOffset={isPercentageStacked ? "expand" : "none"}
            barCategoryGap="10%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" strokeOpacity={0.5} />
            <XAxis
              dataKey={config.xAxis?.field || 'dateFormatted'}
              tick={{ fontSize: 12, fill: '#71717a' }}
              tickFormatter={(value) => formatAxisValue(value, config.xAxis)}
              height={60}
              interval={0}
              angle={-45}
              textAnchor="end"
              axisLine={{ stroke: '#e4e4e7' }}
            />
            <YAxis
              tick={isPercentageStacked ? { fontSize: 12, fill: '#71717a' } : { fontSize: 12, fill: '#71717a' }}
              width={isPercentageStacked ? 60 : 60}
              tickFormatter={isPercentageStacked ? (value) => `${(value * 100).toFixed(0)}%` : undefined}
              domain={isPercentageStacked ? [0, 1] : undefined}
              axisLine={{ stroke: '#e4e4e7' }}
              label={stackedBarYAxisConfig?.label ? {
                value: stackedBarYAxisConfig.label,
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#71717a', fontSize: 12 }
              } : undefined}
            />
            <Tooltip
              content={<CustomTooltip config={config} />}
              cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
            />
            {config.legend?.show && (
              <Legend
                content={renderCustomLegend}
                align={config.legend.align === 'start' ? 'left' : config.legend.align === 'end' ? 'right' : 'center'}
                verticalAlign={config.legend.position === 'top' ? 'top' : 'bottom'}
              />
            )}
            {filteredSeries?.length ? (
              // Multi-series stacked bars
              filteredSeries.map((series, index) => {
                const seriesColor = series.color || colors[index % colors.length];
                const opacity = getSeriesOpacity(series.field);
                return (
                  <Bar
                    key={series.name}
                    dataKey={series.field}
                    fill={seriesColor}
                    fillOpacity={opacity}
                    name={formatOrderTypeValue(series.name)}
                    stackId="stack"
                    label={(config as any).showLabels ? createCustomLabel(config, index, seriesColor, series.field, highlightedSeries, filteredData) : undefined}
                    isAnimationActive={false}
                    animationDuration={0}
                  />
                );
              })
            ) : (
              // Fallback single bar (though stacked-bar should always have multiple series)
              <Bar
                dataKey={stackedBarYAxisConfig?.field || 'value'}
                fill={colors[0]}
                name={formatOrderTypeValue(stackedBarYAxisConfig?.label || 'Value')}
                stackId="stack"
                isAnimationActive={false}
                animationDuration={0}
              />
            )}
          </BarChart>
        );

      case 'area':
        const areaYAxisConfig = Array.isArray(config.yAxis) ? config.yAxis[0] : config.yAxis;
        const primaryColor = colors[0] || '#f97316';
        const isPercentageStack = config.stackType === 'percentage' || config.showAsPercentage;
        // Check if stacking should be applied for area charts when toggled
        const shouldStackArea = config.chartTypeToggle && activeChartType === 'area' ? (config.stackedWhenArea !== false) : config.stacked;

        // Determine if we need dual Y-axes (when enterprise is shown)
        const hasEnterprise = filteredSeries?.some((s: any) => s.field === 'ENTERPRISE');
        const useDualYAxis = hasEnterprise && showEnterpriseToggle;

        return (
          <AreaChart
            data={filteredData}
            margin={{ top: 60, right: 40, bottom: 60, left: 40 }}
            stackOffset={isPercentageStack ? "expand" : "none"}
          >
            <defs>
              <linearGradient id={`area-gradient-${primaryColor.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={primaryColor} stopOpacity={0.4} />
                <stop offset="100%" stopColor={primaryColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" strokeOpacity={0.5} />
            <XAxis
              dataKey={config.xAxis.field}
              tick={{ fontSize: 12, fill: '#71717a' }}
              tickFormatter={(value) => formatAxisValue(value, config.xAxis)}
              height={60}
              interval={0}
              angle={-45}
              textAnchor="end"
              axisLine={{ stroke: '#e4e4e7' }}
            />

            {/* Left Y-Axis - for location lines */}
            <YAxis
              yAxisId="left"
              tick={useDualYAxis || isPercentageStack ? { fontSize: 12, fill: '#71717a' } : false}
              width={useDualYAxis || isPercentageStack ? 60 : 0}
              tickFormatter={isPercentageStack ? (value) => `${(value * 100).toFixed(0)}%` :
                useDualYAxis ? (value) => formatAxisValue(value, areaYAxisConfig) : undefined}
              domain={isPercentageStack ? [0, 1] : undefined}
              axisLine={useDualYAxis || isPercentageStack ? { stroke: '#e4e4e7' } : false}
              label={useDualYAxis ? {
                value: 'Locations',
                angle: -90,
                position: 'insideLeft',
                offset: 10,
                style: { fontSize: 11, fill: '#71717a', fontWeight: 600, textAnchor: 'middle' }
              } : undefined}
            />

            {/* Right Y-Axis - for enterprise line */}
            {useDualYAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12, fill: '#71717a' }}
                width={60}
                tickFormatter={(value) => formatAxisValue(value, areaYAxisConfig)}
                axisLine={{ stroke: '#e4e4e7' }}
                label={{
                  value: 'Enterprise',
                  angle: 90,
                  position: 'insideRight',
                  offset: 10,
                  style: { fontSize: 11, fill: '#71717a', fontWeight: 600, textAnchor: 'middle' }
                }}
              />
            )}

            <Tooltip
              content={<CustomTooltip config={config} useDualYAxis={useDualYAxis} />}
              cursor={{ stroke: primaryColor, strokeWidth: 1, strokeDasharray: '3 3' }}
            />
            {config.legend?.show && (
              <Legend
                content={renderCustomLegend}
                align={config.legend.align === 'start' ? 'left' : config.legend.align === 'end' ? 'right' : 'center'}
                verticalAlign={config.legend.position === 'top' ? 'top' : 'bottom'}
              />
            )}
            {filteredSeries?.length ? (
              // Multi-series area chart
              filteredSeries.map((series, index) => {
                const seriesColor = series.color || colors[index % colors.length];
                const gradientId = `area-gradient-${seriesColor.replace('#', '')}-${index}`;
                const isEnterprise = series.field === 'ENTERPRISE';
                const opacity = getSeriesOpacity(series.field);
                const strokeWidth = getSeriesStrokeWidth(series.field, isEnterprise ? 3 : 2);

                // Assign yAxisId based on whether it's enterprise
                const yAxisId = (useDualYAxis && isEnterprise) ? 'right' : 'left';

                return (
                  <React.Fragment key={series.name}>
                    {!isEnterprise && (
                      <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={seriesColor} stopOpacity={0.4} />
                          <stop offset="100%" stopColor={seriesColor} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                    )}
                    <Area
                      type="monotone"
                      dataKey={series.field}
                      yAxisId={yAxisId}
                      stackId={shouldStackArea ? 'stack' : series.name}
                      stroke={seriesColor}
                      fill={isEnterprise ? 'none' : `url(#${gradientId})`}
                      strokeWidth={strokeWidth}
                      strokeDasharray={(series as any).strokeDasharray}
                      fillOpacity={isEnterprise ? 0 : opacity}
                      strokeOpacity={opacity}
                      name={formatOrderTypeValue(series.name)}
                      label={createCustomLabel(config, index, seriesColor, series.field, highlightedSeries, filteredData)}
                      isAnimationActive={false}
                      animationDuration={0}
                    />
                  </React.Fragment>
                );
              })
            ) : (
              // Simple single-series area chart with gradient
              <Area
                type="monotone"
                dataKey={areaYAxisConfig.field}
                yAxisId="left"
                stroke={primaryColor}
                fill={`url(#area-gradient-${primaryColor.replace('#', '')})`}
                strokeWidth={2}
                fillOpacity={1}
                name={formatOrderTypeValue(areaYAxisConfig.label || 'Value')}
                label={createCustomLabel(config, 0, primaryColor, areaYAxisConfig.field, highlightedSeries, filteredData)}
                isAnimationActive={false}
                animationDuration={0}
              />
            )}
          </AreaChart>
        );

      case 'ranked-band-line':
        // Multi-line ranked band trend chart for payment channels
        const rankedYAxisConfig = Array.isArray(config.yAxis) ? config.yAxis[0] : config.yAxis;

        // Get active metric mode configuration
        const activeMetricMode = config.metricToggle?.modes?.find(m => m.id === metricMode) ||
          config.metricToggle?.modes?.[0];
        const isPercentageMode = activeMetricMode?.showAsPercentage || false;

        // Group payment methods into channels (with optional expansion)
        const groupPaymentChannels = (sourceData: any[]) => {
          if (!config.paymentChannelGrouping?.enabled || !config.paymentChannelGrouping.groups) {
            return sourceData;
          }

          const groups = config.paymentChannelGrouping.groups;

          return sourceData.map(row => {
            const newRow: any = { ...row };

            // First, calculate period total (sum of all payment methods)
            let periodTotal = 0;
            groups.forEach(g => {
              g.fields.forEach(f => {
                if (typeof row[f] === 'number') {
                  periodTotal += row[f];
                }
              });
            });

            groups.forEach(group => {
              const isExpanded =
                (group.name === 'Card' && showCreditCardDetail) ||
                (group.name === 'Third-Party Delivery' && showThirdPartyDetail);

              if (isExpanded) {
                // When expanded, convert individual fields to percentages if in percentage mode
                // Use detailSeries fields if available, otherwise fall back to group.fields
                if (isPercentageMode) {
                  const fieldsToProcess = (group as any).detailSeries
                    ? (group as any).detailSeries.map((ds: any) => ds.field)
                    : group.fields;

                  fieldsToProcess.forEach((field: string) => {
                    const fieldValue = row[field];
                    if (typeof fieldValue === 'number') {
                      newRow[field] = periodTotal > 0 ? (fieldValue / periodTotal) * 100 : 0;
                    }
                  });
                }
                // If not percentage mode, individual fields keep their dollar values (already in row)
              } else {
                // When collapsed, sum the group and create a single channel field
                let groupTotal = 0;
                group.fields.forEach(field => {
                  if (typeof row[field] === 'number') {
                    groupTotal += row[field];
                  }
                });

                if (isPercentageMode) {
                  newRow[group.name] = periodTotal > 0 ? (groupTotal / periodTotal) * 100 : 0;
                } else {
                  newRow[group.name] = groupTotal;
                }
              }
            });

            return newRow;
          });
        };

        // Use original data, not filteredData, to ensure all payment fields are present
        const channelData = groupPaymentChannels(data);

        // Create series from channel groups (sorted by rank), with conditional expansion
        const channelSeries = config.paymentChannelGrouping?.enabled && config.paymentChannelGrouping.groups
          ? [...config.paymentChannelGrouping.groups]
            .sort((a, b) => a.rank - b.rank)
            .flatMap((group: any) => {
              // Check if this group should be expanded
              const shouldExpand =
                (group.name === 'Card' && showCreditCardDetail) ||
                (group.name === 'Third-Party Delivery' && showThirdPartyDetail);

              if (shouldExpand && group.detailSeries) {
                // Return individual series for expanded view
                return group.detailSeries.map((detail: any, idx: number) => ({
                  name: detail.name,
                  field: detail.field,
                  color: detail.color || group.color,
                  strokeWidth: detail.strokeWidth || group.strokeWidth - 0.5,
                  opacity: detail.opacity || group.opacity * 0.8,
                  showAreaFill: false,
                  areaOpacity: 0
                }));
              } else {
                // Return grouped series
                return [{
                  name: group.name,
                  field: group.name,
                  color: group.color,
                  strokeWidth: group.strokeWidth,
                  opacity: group.opacity,
                  showAreaFill: group.showAreaFill,
                  areaOpacity: group.areaOpacity || 0.2
                }];
              }
            })
          : [];

        // Calculate max value for Y-axis domain (for Net Sales mode only)
        let maxValue = 0;
        if (!isPercentageMode && channelData.length > 0 && channelSeries.length > 0) {
          channelSeries.forEach(series => {
            channelData.forEach(row => {
              const value = row[series.field];
              if (typeof value === 'number' && value > maxValue) {
                maxValue = value;
              }
            });
          });
        }

        return (
          <ComposedChart data={channelData} margin={{ top: 60, right: 40, bottom: 60, left: 40 }}>
            <defs>
              {channelSeries
                .filter(series => series.showAreaFill)
                .map((series, index) => {
                  const gradientId = `ranked-gradient-${series.field.replace(/\s+/g, '-')}`;
                  return (
                    <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={series.color} stopOpacity={series.areaOpacity} />
                      <stop offset="100%" stopColor={series.color} stopOpacity={0} />
                    </linearGradient>
                  );
                })}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" strokeOpacity={0.5} />
            <XAxis
              dataKey={config.xAxis?.field || 'dateFormatted'}
              tick={{ fontSize: 12, fill: '#71717a' }}
              tickFormatter={(value) => formatAxisValue(value, config.xAxis)}
              height={60}
              interval={0}
              angle={-45}
              textAnchor="end"
              axisLine={{ stroke: '#e4e4e7' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#71717a' }}
              width={60}
              tickFormatter={(value) => {
                if (isPercentageMode) {
                  return `${value.toFixed(0)}%`;
                }
                return formatAxisValue(value, activeMetricMode?.yAxis || rankedYAxisConfig);
              }}
              domain={isPercentageMode ? [0, 100] : maxValue > 0 ? [0, maxValue * 1.1] : [0, 'auto']}
              axisLine={{ stroke: '#e4e4e7' }}
              label={activeMetricMode?.yAxis?.label ? {
                value: activeMetricMode.yAxis.label,
                angle: -90,
                position: 'insideLeft',
                offset: 10,
                style: { fontSize: 11, fill: '#71717a', fontWeight: 600, textAnchor: 'middle' }
              } : undefined}
            />
            <Tooltip
              content={<CustomTooltip config={{
                ...config,
                showAsPercentage: isPercentageMode,
                yAxis: activeMetricMode?.yAxis || config.yAxis
              }} />}
              cursor={{ stroke: '#f97316', strokeWidth: 1, strokeDasharray: '3 3' }}
            />
            {config.legend?.show && (
              <Legend
                content={renderCustomLegend}
                align={config.legend.align === 'start' ? 'left' : config.legend.align === 'end' ? 'right' : 'center'}
                verticalAlign={config.legend.position === 'top' ? 'top' : 'bottom'}
                wrapperStyle={{ paddingTop: '20px' }}
              />
            )}
            {channelSeries.map((series, index) => {
              const hasAreaFill = series.showAreaFill;
              const gradientId = `ranked-gradient-${series.field.replace(/\s+/g, '-')}`;
              const seriesOpacity = highlightedSeries === null || highlightedSeries === series.field
                ? series.opacity
                : series.opacity * 0.3;

              if (hasAreaFill) {
                // Use Area component for series with fill
                return (
                  <Area
                    key={series.name}
                    type="monotone"
                    dataKey={series.field}
                    stroke={series.color}
                    fill={`url(#${gradientId})`}
                    strokeWidth={series.strokeWidth}
                    strokeOpacity={seriesOpacity}
                    fillOpacity={seriesOpacity}
                    name={series.name}
                    dot={{ r: 3, opacity: seriesOpacity }}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                    animationDuration={0}
                  />
                );
              } else {
                // Use Line component for series without fill
                return (
                  <Line
                    key={series.name}
                    type="monotone"
                    dataKey={series.field}
                    stroke={series.color}
                    strokeWidth={series.strokeWidth}
                    strokeOpacity={seriesOpacity}
                    name={series.name}
                    dot={{ r: 3, opacity: seriesOpacity }}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                    animationDuration={0}
                  />
                );
              }
            })}
          </ComposedChart>
        );

      case 'pie':
        const pieData = data.map((item, index) => ({
          ...item,
          fill: colors[index % colors.length]
        }));

        // Smart dataKey detection for pie charts (handles missing yAxis)
        let pieDataKey = 'value'; // fallback

        if (config.yAxis) {
          pieDataKey = getYAxisConfig(config.yAxis, 0).field;
        } else if (config.metrics && config.metrics.length > 0) {
          pieDataKey = config.metrics[0]; // use first metric
        } else if (effectiveSeries && effectiveSeries.length > 0) {
          pieDataKey = effectiveSeries[0].field; // use first series field
        } else if (data.length > 0) {
          // Auto-detect numeric field from data
          const firstRow = data[0];
          const numericFields = Object.keys(firstRow).filter(key =>
            typeof firstRow[key] === 'number' && key !== 'fill'
          );
          if (numericFields.length > 0) {
            pieDataKey = numericFields[0];
          }
        }

        return (
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${formatOrderTypeValue(name)} ${(percent * 100).toFixed(0)}%`}
              outerRadius={120}
              fill="#8884d8"
              dataKey={pieDataKey}
              nameKey={config.xAxis?.field || 'name'}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip config={config} />}
              formatter={(value: any, name: any) => [value, formatOrderTypeValue(name)]}
            />
            {config.legend?.show && (
              <Legend
                content={renderCustomLegend}
              />
            )}
          </PieChart>
        );

      case 'doughnut':
        const doughnutData = data.map((item, index) => ({
          ...item,
          fill: colors[index % colors.length]
        }));

        // Smart dataKey detection for doughnut charts (same as pie)
        let doughnutDataKey = 'value'; // fallback

        if (config.yAxis) {
          doughnutDataKey = getYAxisConfig(config.yAxis, 0).field;
        } else if (config.metrics && config.metrics.length > 0) {
          doughnutDataKey = config.metrics[0]; // use first metric
        } else if (effectiveSeries && effectiveSeries.length > 0) {
          doughnutDataKey = effectiveSeries[0].field; // use first series field
        } else if (data.length > 0) {
          // Auto-detect numeric field from data
          const firstRow = data[0];
          const numericFields = Object.keys(firstRow).filter(key =>
            typeof firstRow[key] === 'number' && key !== 'fill'
          );
          if (numericFields.length > 0) {
            doughnutDataKey = numericFields[0];
          }
        }

        return (
          <PieChart>
            <Pie
              data={doughnutData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${formatOrderTypeValue(name)} ${(percent * 100).toFixed(0)}%`}
              outerRadius={120}
              innerRadius={60}  // This makes it a doughnut
              fill="#8884d8"
              dataKey={doughnutDataKey}
              nameKey={config.xAxis?.field || 'name'}
            >
              {doughnutData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip config={config} />}
              formatter={(value: any, name: any) => [value, formatOrderTypeValue(name)]}
            />
            {config.legend?.show && (
              <Legend
                content={renderCustomLegend}
              />
            )}
          </PieChart>
        );

      case 'scatter':
        const yAxisConfig = Array.isArray(config.yAxis) ? config.yAxis[0] : config.yAxis;
        return (
          <ScatterChart data={data} margin={{ top: 60, right: 40, bottom: 60, left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              type="number"
              dataKey={config.xAxis.field}
              tick={{ fontSize: 14, fill: '#666' }}
              tickFormatter={(value) => formatAxisValue(value, config.xAxis)}
              label={{ value: config.xAxis?.label || '', position: 'insideBottom', offset: -10, style: { fontSize: '14px' } }}
            />
            <YAxis
              type="number"
              dataKey={yAxisConfig.field}
              tick={false}
              width={0}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip config={activeConfig} />} />
            {config.legend?.show && (
              <Legend
                wrapperStyle={{ fontSize: '14px', paddingTop: '8px', paddingBottom: '24px', marginBottom: '12px', cursor: 'pointer' }}
                align={config.legend.align === 'start' ? 'left' : config.legend.align === 'end' ? 'right' : 'center'}
                verticalAlign={config.legend.position === 'top' ? 'top' : 'bottom'}
                formatter={(value: string) => formatOrderTypeValue(value)}
                onClick={handleLegendClick}
              />
            )}
            <Scatter
              name="Data Points"
              data={data}
              fill={colors[0]}
            />
          </ScatterChart>
        );

      case 'mixed':
        // Get Y-axis configurations for dual axes
        const leftYAxis = getYAxisConfig(config.yAxis, 0);
        const rightYAxis = getYAxisConfig(config.yAxis, 1);

        return (
          <ComposedChart data={filteredData} margin={{ top: 60, right: 40, bottom: 60, left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey={config.xAxis.field}
              tick={{ fontSize: 14, fill: '#666' }}
              tickFormatter={(value) => formatAxisValue(value, config.xAxis)}
              height={60}
              interval={0}
              angle={-45}
              textAnchor="end"
            />
            {/* Left Y-Axis */}
            <YAxis
              yAxisId="left"
              tick={false}
              width={0}
              axisLine={false}
            />
            {/* Right Y-Axis */}
            {rightYAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={false}
                width={0}
                axisLine={false}
              />
            )}
            <Tooltip content={<CustomTooltip config={activeConfig} />} />
            {config.legend?.show && (
              <Legend
                wrapperStyle={{ fontSize: '14px', paddingTop: '8px', paddingBottom: '24px', marginBottom: '12px', cursor: 'pointer' }}
                align={config.legend.align === 'start' ? 'left' : config.legend.align === 'end' ? 'right' : 'center'}
                verticalAlign={config.legend.position === 'top' ? 'top' : 'bottom'}
                formatter={(value: string) => formatOrderTypeValue(value)}
                onClick={handleLegendClick}
              />
            )}
            {filteredSeries?.map((series, index) => {
              const yAxisId = (series as any).yAxisIndex === 1 ? 'right' : 'left';
              const color = series.color || colors[index % colors.length];
              const formattedName = formatOrderTypeValue(series.name);
              const opacity = getSeriesOpacity(series.field);
              const baseStrokeWidth = (series as any).lineWidth || (series as any).strokeWidth || 2;
              const strokeWidth = getSeriesStrokeWidth(series.field, baseStrokeWidth);

              if (series.type === 'bar') {
                return (
                  <Bar
                    key={series.name}
                    yAxisId={yAxisId}
                    dataKey={series.field}
                    fill={color}
                    fillOpacity={opacity}
                    name={formattedName}
                    stackId={config.stacked ? 'stack' : undefined}
                  />
                );
              } else if (series.type === 'line') {
                return (
                  <Line
                    key={series.name}
                    yAxisId={yAxisId}
                    type="monotone"
                    dataKey={series.field}
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={(series as any).strokeDasharray}
                    strokeOpacity={opacity}
                    name={formattedName}
                    dot={{ r: 3, opacity }}
                    activeDot={{ r: 5, opacity }}
                  />
                );
              } else if (series.type === 'area') {
                const areaGradientId = `composed-area-gradient-${color.replace('#', '')}-${index}`;
                return (
                  <React.Fragment key={series.name}>
                    <defs>
                      <linearGradient id={areaGradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area
                      yAxisId={yAxisId}
                      type="monotone"
                      dataKey={series.field}
                      stackId={config.stacked ? 'stack' : series.name}
                      stroke={color}
                      strokeWidth={strokeWidth}
                      strokeOpacity={opacity}
                      fill={`url(#${areaGradientId})`}
                      fillOpacity={opacity}
                      name={formattedName}
                    />
                  </React.Fragment>
                );
              }
              return null;
            })}
          </ComposedChart>
        );

      default:
        return (
          <Alert severity="warning">
            Unsupported chart type: {config.chartType}
          </Alert>
        );
    }
  };

  // Render top/bottom toggle when splitLocations is enabled (but not when showTopAndBottom is true)
  const renderTopBottomToggle = () => {
    if (!config.splitLocations || config.showTopAndBottom) return null;

    const { topCount, bottomCount } = getTopBottomCounts();

    return (
      <ToggleButtonGroup
        value={topBottomView}
        exclusive
        onChange={(event, newValue) => {
          if (newValue !== null) {
            setTopBottomView(newValue);
          }
        }}
        size="small"
        sx={{
          height: 32,
          '& .MuiToggleButton-root': {
            px: 1.5,
            py: 0.5,
            fontSize: '0.8125rem',
            fontWeight: 500,
            textTransform: 'none',
            border: '1px solid #e0e0e0',
            color: '#64748b',
            '&.Mui-selected': {
              backgroundColor: '#f97316',
              color: '#fff',
              borderColor: '#f97316',
              '&:hover': {
                backgroundColor: '#ea580c',
              }
            },
            '&:hover': {
              backgroundColor: '#f1f5f9',
            }
          }
        }}
      >
        <ToggleButton value="top">
          <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />
          Top {topCount}
        </ToggleButton>
        <ToggleButton value="bottom">
          <TrendingDown sx={{ fontSize: 16, mr: 0.5 }} />
          Bottom {bottomCount}
        </ToggleButton>
      </ToggleButtonGroup>
    );
  };

  // Render enterprise toggle when showEnterprise is enabled (but not when showTopAndBottom is true)
  const renderEnterpriseToggle = () => {
    if (!config.showEnterprise || config.showTopAndBottom) return null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
        <MuiTooltip title={showEnterpriseToggle ? "Hide Enterprise Line" : "Show Enterprise Line"} arrow>
          <Box
            onClick={() => setShowEnterpriseToggle(!showEnterpriseToggle)}
            sx={{
              px: 1.5,
              py: 0.5,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.8125rem',
              fontWeight: 500,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: showEnterpriseToggle ? '#1f2937' : '#e0e0e0',
              backgroundColor: showEnterpriseToggle ? '#1f2937' : '#fff',
              color: showEnterpriseToggle ? '#fff' : '#64748b',
              borderRadius: 1,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: showEnterpriseToggle ? '#374151' : '#f1f5f9',
                borderColor: showEnterpriseToggle ? '#374151' : '#cbd5e1',
              }
            }}
          >
            {showEnterpriseToggle ? (
              <Visibility sx={{ fontSize: 16 }} />
            ) : (
              <VisibilityOff sx={{ fontSize: 16 }} />
            )}
            Enterprise
          </Box>
        </MuiTooltip>
        <MuiTooltip
          title="Enterprise line uses a separate Y-axis scale on the right to maintain visibility of individual location trends"
          arrow
          placement="top"
        >
          <InfoOutlined sx={{ fontSize: 16, color: '#94a3b8', cursor: 'help' }} />
        </MuiTooltip>
      </Box>
    );
  };

  // Render chart type toggle when chartTypeToggle is enabled
  const renderChartTypeToggle = () => {
    if (!config.chartTypeToggle || !config.toggleOptions || config.toggleOptions.length < 2) return null;

    return (
      <ToggleButtonGroup
        value={activeChartType}
        exclusive
        onChange={(event, newType) => {
          if (newType !== null) {
            setActiveChartType(newType);
          }
        }}
        size="small"
        sx={{
          height: 32,
          backgroundColor: '#f8fafc',
          borderRadius: '6px',
          padding: '2px',
          border: '1px solid #e2e8f0',
          '& .MuiToggleButtonGroup-grouped': {
            margin: 0,
            border: 0,
            borderRadius: '4px !important',
            '&:not(:first-of-type)': {
              marginLeft: '4px',
            },
          },
          '& .MuiToggleButton-root': {
            padding: '4px 12px',
            fontSize: '13px',
            fontWeight: 500,
            textTransform: 'none',
            color: '#64748b',
            backgroundColor: 'transparent',
            border: 'none',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            '&.Mui-selected': {
              backgroundColor: '#ffffff',
              color: '#0f172a',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: '#ffffff',
              },
            },
            '&:hover': {
              backgroundColor: '#f1f5f9',
              color: '#475569',
            },
            '& .MuiSvgIcon-root': {
              fontSize: '16px',
            },
          },
        }}
      >
        {config.toggleOptions.map((option) => (
          <ToggleButton key={option} value={option}>
            {option === 'line' ? (
              <>
                <ShowChart sx={{ fontSize: 16 }} />
                Line
              </>
            ) : option === 'area' ? (
              <>
                <StackedLineChart sx={{ fontSize: 16 }} />
                Area
              </>
            ) : (
              option.charAt(0).toUpperCase() + option.slice(1)
            )}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  // Render metric toggle for ranked-band-line charts
  const renderMetricToggle = () => {
    if (!config.metricToggle?.enabled || !config.metricToggle.modes || config.metricToggle.modes.length < 2) return null;

    return (
      <ToggleButtonGroup
        value={metricMode}
        exclusive
        onChange={(event, newValue) => {
          if (newValue !== null) {
            setMetricMode(newValue);
          }
        }}
        size="small"
        sx={{
          height: 32,
          marginLeft: 1,
          '& .MuiToggleButton-root': {
            px: 1.5,
            py: 0.5,
            fontSize: '0.8125rem',
            fontWeight: 500,
            textTransform: 'none',
            border: '1px solid #e2e8f0',
            color: '#64748b',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
              backgroundColor: '#ffffff',
              color: '#0f172a',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: '#ffffff',
              },
            },
            '&:hover': {
              backgroundColor: '#f1f5f9',
              color: '#475569',
            },
          },
        }}
      >
        {config.metricToggle.modes.map((mode) => (
          <ToggleButton key={mode.id} value={mode.id}>
            {mode.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  // Render payment channel detail expansion toggles (as Switch components)
  const renderChannelExpansionToggles = () => {
    if (config.chartType !== 'ranked-band-line' || !config.paymentChannelGrouping?.enabled) return null;

    return (
      <Box sx={{
        display: 'flex',
        gap: 3,
        mb: 2,
        ml: 0,
        alignItems: 'center'
      }}>
        {/* Credit Card Detail Toggle */}
        <MuiTooltip
          title="Expand to view individual credit card brands (Visa, MasterCard, Amex, Discover)"
          arrow
          placement="top"
        >
          <FormControlLabel
            control={
              <Switch
                checked={showCreditCardDetail}
                onChange={(e) => handleCreditCardDetailChange(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase': {
                    '&.Mui-checked': {
                      color: '#ffffff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#f97316',
                        opacity: 1,
                      },
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#f97316',
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#e4e4e7',
                    opacity: 1,
                  },
                }}
              />
            }
            label="Credit Card Detail"
            sx={{
              '& .MuiFormControlLabel-label': {
                fontFamily: "'Inter', sans-serif",
                fontSize: '14px',
                fontWeight: 400,
                color: showCreditCardDetail ? '#f97316' : '#18181b',
              },
            }}
          />
        </MuiTooltip>

        {/* Third-Party Delivery Detail Toggle */}
        <MuiTooltip
          title="Expand to view individual delivery platforms (DoorDash, Uber Eats, Grubhub)"
          arrow
          placement="top"
        >
          <FormControlLabel
            control={
              <Switch
                checked={showThirdPartyDetail}
                onChange={(e) => handleThirdPartyDetailChange(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase': {
                    '&.Mui-checked': {
                      color: '#ffffff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#dc2626',
                        opacity: 1,
                      },
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#dc2626',
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#e4e4e7',
                    opacity: 1,
                  },
                }}
              />
            }
            label="Third-Party Delivery Detail"
            sx={{
              '& .MuiFormControlLabel-label': {
                fontFamily: "'Inter', sans-serif",
                fontSize: '14px',
                fontWeight: 400,
                color: showThirdPartyDetail ? '#dc2626' : '#18181b',
              },
            }}
          />
        </MuiTooltip>
      </Box>
    );
  };

  const showHeader = chartFilters.length > 0 || config.splitLocations || config.showEnterprise || config.showTopAndBottom || config.chartTypeToggle || config.metricToggle?.enabled || visualization.title;

  // Check if dual Y-axis is active for rendering axis tooltips
  const isDualYAxisActive = (effectiveChartType === 'line' || effectiveChartType === 'area') &&
    config.showEnterprise &&
    showEnterpriseToggle;

  return (
    <ChartContainer className={className}>
      {chartHoverStyles}
      {showHeader && (
        <ChartHeader>
          {visualization.title && (
            <Box>
              <ChartTitle>{visualization.title}</ChartTitle>
              {visualization.subtitle && (
                <ChartSubtitle>
                  {visualization.subtitle}
                </ChartSubtitle>
              )}
              {!visualization.subtitle && (config.stackType === 'percentage' || config.showAsPercentage) && (
                <ChartSubtitle>
                  Percent of total sales per period. A value of 100% indicates a single contributing order type.
                  <MuiTooltip
                    title="Percentages are calculated independently for each time period based on total sales. Order types may display 100% when they are the only contributor during that period."
                    arrow
                    placement="top"
                  >
                    <InfoOutlined sx={{ fontSize: 16, cursor: 'help', color: '#71717a' }} />
                  </MuiTooltip>
                </ChartSubtitle>
              )}
            </Box>
          )}
          <Box sx={{ flex: 1 }} /> {/* Spacer to push toggles right */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {renderMetricToggle()}
            {renderChartTypeToggle()}
            {renderTopBottomToggle()}
            {renderEnterpriseToggle()}
            {renderChartFilters()}
          </Box>
        </ChartHeader>
      )}
      {/* Channel Expansion Toggles (positioned between header and chart) */}
      {renderChannelExpansionToggles()}
      <ChartWrapper style={{ position: 'relative' }}>
        {/* Y-Axis Info Tooltips */}
        {isDualYAxisActive && (
          <>
            {/* Left Y-Axis Tooltip */}
            <MuiTooltip
              title="Individual location values - each location's performance shown separately"
              arrow
              placement="right"
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  cursor: 'help',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  }
                }}
              >
                <InfoOutlined sx={{ fontSize: 14, color: '#3b82f6' }} />
              </Box>
            </MuiTooltip>

            {/* Right Y-Axis Tooltip */}
            <MuiTooltip
              title="Enterprise total - sum of all locations combined at each time point"
              arrow
              placement="left"
            >
              <Box
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  cursor: 'help',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  }
                }}
              >
                <InfoOutlined sx={{ fontSize: 14, color: '#1f2937' }} />
              </Box>
            </MuiTooltip>
          </>
        )}
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </ChartWrapper>
    </ChartContainer>
  );
}; 